import { useId } from 'react';

export interface ChipOption {
  value: string;
  label: string;
}

export interface ChipGroupProps {
  label: string;
  options: ChipOption[];
  value?: string;
  onChange: (value: string) => void;
  error?: string;
  className?: string;
  /**
   * Lay the chips out as an even grid with this many per row (2 per row on
   * phones), e.g. 4 for the eight Total Revenue buckets in a side panel.
   * Omitted = one row that wraps only when it runs out of room.
   */
  columns?: 2 | 3 | 4;
}

// Literal class names: Tailwind only generates classes it can find verbatim.
const GRID_COLUMNS = {
  2: 'grid grid-cols-2',
  3: 'grid grid-cols-2 sm:grid-cols-3',
  4: 'grid grid-cols-2 sm:grid-cols-4',
} as const;

/**
 * Single-select pill chip row (e.g. Total Revenue buckets on create-startup
 * Step 1). Selected chip is navy, unselected chips are pale green. Chips share
 * the row equally, but never shrink below their label: in a narrow container
 * (e.g. a profile side panel) they wrap onto more rows instead of overlapping.
 */
export function ChipGroup({
  label,
  options,
  value,
  onChange,
  error,
  className = '',
  columns,
}: ChipGroupProps) {
  const errorId = useId();
  const layout = columns ? GRID_COLUMNS[columns] : 'flex flex-wrap';

  return (
    <div className="flex flex-col gap-1">
      <div
        role="radiogroup"
        aria-label={label}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        className={`${layout} gap-3 ${className}`.trim()}
      >
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(option.value)}
              className={`rounded-button focus-visible:ring-dkgreen min-w-fit flex-1 px-2 py-3 font-sans text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:outline-none ${
                selected ? 'bg-teal text-white' : 'bg-green/10 text-dkgreen hover:bg-green/20'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {error && (
        <p id={errorId} role="alert" className="text-danger pl-1 text-xs">
          {error}
        </p>
      )}
    </div>
  );
}
